import { Node } from "@xyflow/react";
import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { ServiceIcon, Text, Typeahead } from "@zapier/design-system";
import { Service, takeSnapshotAtom } from "@zapier/canvas-state";

import useUpdateNode from "../../../hooks/useUpdateNode";
import getAppIconUrl from "../../../utils/getAppIconUrl";
import useServiceById from "../../../hooks/useServiceById";
import cleanUpAppId from "../../../utils/cleanUpAppId";
import { determineAssetType } from "../../../utils/determineAssetType";

import { useAppSearchData } from "./useAppSearchData";
import styles from "./AppTypeahead.module.css";

// TODO: Change copy to "App(s)" when we support multiple apps
const APP_INPUT_LABEL = "App";

type ServiceResponse = {
  currentImplementation: {
    id: string;
  };
  name: string;
};

export function AppTypeahead({ node }: { node: Node }) {
  const updateNodeAndSave = useUpdateNode();
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const { apps, loading, onSearch, searchQuery, setSearchQuery } =
    useAppSearchData({
      limit: 10,
      searchDebounceDelay: 300,
      excludeUpcoming: true,
      includeIsAppAdmin: true,
      includeIsAppCollaborator: true,
      apiHostname: "", // relative queries
    });

  const onChangeApp = useCallback(
    (service: ServiceResponse | null) => {
      takeSnapshot();
      if (service) {
        setSearchQuery("");

        const currentAppId = (node.data.services as ServiceResponse[])?.[0]
          ?.currentImplementation.id;
        if (currentAppId === service.currentImplementation.id) {
          // We don't want to reset the color for asset nodes to default
          return;
        }

        const assetType = determineAssetType(
          [
            {
              currentImplementation: {
                id: service.currentImplementation.id,
              },
            },
          ],
          "default",
        );

        return updateNodeAndSave({
          id: node.id,
          data: {
            // Read by the backend to send to Zap Guesser
            app: service.name,
            services: [
              {
                currentImplementation: {
                  id: service.currentImplementation.id,
                },
              },
            ],
            ...(assetType ? { color: undefined } : {}), // Clear color in order to fall back to default asset node colors
          },
        });
      }
    },
    [
      takeSnapshot,
      setSearchQuery,
      node.data.services,
      node.id,
      updateNodeAndSave,
    ],
  );

  return (
    <>
      <Text type="SmallPrint2">{APP_INPUT_LABEL}</Text>
      <Typeahead
        placeholder="Search"
        menuAriaLabel="List of available apps"
        aria-label="Typeahead to choose apps"
        autoComplete="off"
        getLabelForItem={(item) => item.name}
        renderIcon={(item) => <AppItemIcon item={item} />}
        items={apps}
        inputValue={searchQuery}
        onChange={onChangeApp}
        openOnFocus={true}
        keepInputValueOnBlur={true}
        onInputValueChange={onSearch}
        isBusy={loading}
        selectedItem={null}
        filterItems={(_query, items) => items}
      />
    </>
  );
}

export const AppItemIcon = ({ item }: { item: Service }) => {
  const getService = useServiceById();

  const appId = item.currentImplementation.id;
  const service = getService(appId);
  const serviceName = service?.name ?? cleanUpAppId(appId);
  const images = service?.images;
  const src = getAppIconUrl(appId, images, 30);

  return (
    <span className={styles.serviceIcon} data-zds>
      <ServiceIcon
        alt={`${serviceName} icon`}
        hasBorder={false}
        size="small"
        hasShadow={false}
        serviceName={serviceName}
        src={src}
      />
    </span>
  );
};
